// Funções puras do inbox do chat com clientes. CommonJS (ver utils/messages.js).

const INBOX_SCOPE_OWNED = 'owned'
const INBOX_SCOPE_INVITED = 'invited'
const INBOX_SCOPES = [INBOX_SCOPE_OWNED, INBOX_SCOPE_INVITED]

const moveToTop = (ids, id) => [id].concat((ids || []).filter(existing => existing !== id))

// Página 0 substitui a lista; páginas seguintes acrescentam sem repetir.
const mergeInboxIds = (current, incoming, append) => {
  if (!append) return (incoming || []).slice()
  const result = (current || []).slice()
  const additions = incoming || []
  additions.forEach(id => {
    if (!result.includes(id)) result.push(id)
  })
  return result
}

// Soma das não lidas das conversas carregadas em qualquer escopo, sem contar duas
// vezes a mesma conversa (pode estar em "Minhas" e "Direcionadas").
const totalUnread = (conversationsById, idLists) => {
  const seen = new Set()
  let total = 0
  const lists = idLists || []
  lists.forEach(ids => {
    (ids || []).forEach(id => {
      if (seen.has(id)) return
      seen.add(id)
      const conversation = conversationsById && conversationsById[id]
      total += (conversation && Number(conversation.unreadCount)) || 0
    })
  })
  return total
}

const formatUnreadBadge = count => {
  const value = Number(count) || 0
  if (value <= 0) return null
  return value > 99 ? '99+' : String(value)
}

const PREVIEW_MAX_CODE_POINTS = 120
// Mesmos caracteres do `\s` do Java usado no backend (LastMessagePreview.truncate).
const PREVIEW_WHITESPACE = /[ \t\n\v\f\r]+/g

/**
 * Prévia do inbox, idêntica à do backend (LastMessagePreview.truncate): espaços
 * colapsados, trim, até 120 code points (não parte emoji) e "…" se cortado.
 * Corpo ausente (mensagem redigida) → undefined.
 */
const buildPreview = body => {
  if (typeof body !== 'string') return undefined
  const flat = body.replace(PREVIEW_WHITESPACE, ' ').trim()
  const codePoints = Array.from(flat)
  if (codePoints.length <= PREVIEW_MAX_CODE_POINTS) return flat
  return `${codePoints.slice(0, PREVIEW_MAX_CODE_POINTS).join('').trimEnd()}…`
}

// MessageResponse (frame `message.created` / resposta do envio) → LastMessagePreview.
const toLastMessage = message => {
  const lastMessage = {
    seq: message.seq,
    senderType: message.senderType,
    createdAt: message.createdAt,
  }
  if (message.senderName) lastMessage.senderName = message.senderName
  if (message.senderUnitLabel) lastMessage.senderUnitLabel = message.senderUnitLabel
  const preview = buildPreview(message.body)
  if (preview !== undefined) lastMessage.preview = preview
  return lastMessage
}

/**
 * Linha secundária do item do inbox: prévia da última mensagem (staff com o nome
 * de quem enviou); sem `lastMessage`, cai para o responsável.
 */
const inboxSubtitle = conversation => {
  const lastMessage = conversation && conversation.lastMessage
  if (lastMessage) {
    const text = typeof lastMessage.preview === 'string' ? lastMessage.preview : 'Mensagem removida'
    if (lastMessage.senderType === 'STAFF' && lastMessage.senderName) return `${lastMessage.senderName}: ${text}`
    return text
  }
  const owner = conversation && conversation.owner
  if (owner) return `Responsável: ${[owner.name, owner.unitLabel].filter(Boolean).join(' · ')}`
  return 'Sem responsável'
}

// "Lida pelo cliente": o maior entre o que veio no REST (customerLastReadSeq) e no WS (read.updated).
const resolveCustomerReadSeq = (conversation, wsLastReadSeq) => Math.max(
  Number(conversation && conversation.customerLastReadSeq) || 0,
  Number(wsLastReadSeq) || 0,
)

/**
 * Aplica um `message.created` ao resumo da conversa. Só conta como não lida se a
 * mensagem é nova (seq acima do lastSeq conhecido — protege contra frame repetido),
 * não é do próprio usuário e a conversa não está aberta e visível.
 */
const applyIncomingMessage = (conversation, message, { isOpenAndVisible, isOwn } = {}) => {
  const lastSeq = Number(conversation.lastSeq) || 0
  const isNew = typeof message.seq === 'number' && message.seq > lastSeq
  if (!isNew) return conversation
  const shouldCount = !isOpenAndVisible && !isOwn
  return {
    ...conversation,
    lastSeq: message.seq,
    lastMessageAt: message.createdAt || conversation.lastMessageAt,
    lastMessage: toLastMessage(message),
    unreadCount: shouldCount ? (Number(conversation.unreadCount) || 0) + 1 : (Number(conversation.unreadCount) || 0),
  }
}

module.exports = {
  INBOX_SCOPE_OWNED,
  INBOX_SCOPE_INVITED,
  INBOX_SCOPES,
  moveToTop,
  mergeInboxIds,
  totalUnread,
  formatUnreadBadge,
  applyIncomingMessage,
  PREVIEW_MAX_CODE_POINTS,
  buildPreview,
  toLastMessage,
  inboxSubtitle,
  resolveCustomerReadSeq,
}
